import React, { useEffect, useState } from 'react';
import { Alert, Card, Heading, TextButton } from '@zillow/constellation';
import { WizardContent, WizardProvider } from '@zillow/zrs-rental-applications';
import { checkIsBasedOnScreening, Steps } from './DeclineApplicationPage.utils';
import { ReasonsStep } from './ReasonsStep';
import { StepNavigation } from './StepNavigation';
import { PreviewStep } from './PreviewStep';
import { ConfirmationStep } from './ConfirmationStep';
import { useSelector } from 'react-redux';
import { isApplicationReviewStatusLoadingSelector, listingAliasSelector } from '../../selectors';
import LoadingScreen from '../../components/LoadingScreen/LoadingScreen';
import WithdrawnErrorStep from './WithdrawnErrorStep/WithdrawnErrorStep';
import { OverviewLink } from './OverviewLink';
import useQueryVariables from '../../hooks/useQueryVariables';
import { getLandlordReviewPath, URLS } from '../../constants/urls';
import { useHistory } from 'react-router-dom';
import { APPLICATION_SUMMARY_QUERY } from '../../constants/queries';
import { useZgGraphqlQuery } from '../../hooks/useGraphqlQuery';
import useGetDeclineReasons from '../../utils/apis/useGetDeclineReasons';
import { applicationOverallStatusTypes } from '../../constants/statusConstants';

const CONDITIONAL_APPROVAL_LEGAL_NOTICE =
  'Since you’ve marked this application as conditionally approved, there are a few more steps you may need to take before you can decline it. This may include performing a detailed review of the applicant’s criminal history to ensure that your decision to decline is fair and justified, a written notice of denial, and/or a copy of the background check used to make this decision.';

const DeclineApplicationPage = () => {
  const listingAlias = useSelector(listingAliasSelector);
  const { applicationId } = useQueryVariables();
  const history = useHistory();
  const [selectedDeclineReasons, setSelectedDeclineReasons] = useState(new Set());
  const isLoadingApplicationReview = useSelector(isApplicationReviewStatusLoadingSelector);
  const { data: allDeclineReasons, isLoading: isLoadingDeclineReasons } = useGetDeclineReasons({
    select(response) {
      return response.data?.declineReasons;
    },
  });
  const isBasedOnScreening = !isLoadingDeclineReasons
    ? checkIsBasedOnScreening(selectedDeclineReasons, allDeclineReasons)
    : false;
  const { data: overallStatus, loading: isLoadingApplication } = useZgGraphqlQuery(
    APPLICATION_SUMMARY_QUERY,
    { listingAlias, applicationId },
    {
      select(data) {
        return data.rentalListing.landlordRentalApplication.overallStatus;
      },
    },
  );
  const isDeclined = overallStatus === applicationOverallStatusTypes.DECLINED;
  const isConditionallyApproved = overallStatus === applicationOverallStatusTypes.CONDITIONALLY_APPROVED;
  useEffect(() => {
    if (isDeclined) {
      history.push(getLandlordReviewPath(listingAlias, applicationId));
    }
  }, [isDeclined, history, applicationId, listingAlias]);

  if (isLoadingApplication || isLoadingApplicationReview || isLoadingDeclineReasons) {
    return <LoadingScreen className="rental-application__loading-screen--transparent-bg" show isLoading />;
  }

  return (
    <WizardProvider>
      <OverviewLink />
      <Heading level={3} marginBottom="md" fontFamily="serif" paddingX={{ default: 'sm', md: 0 }}>
        Decline application
      </Heading>
      {isConditionallyApproved && (
        <Alert
          marginBottom="md"
          appearance="warning"
          actionButton={
            <TextButton as="a" href={URLS.COMPLIANCE_LEARN_MORE} target="_blank">
              Review local requirements
            </TextButton>
          }
          body={CONDITIONAL_APPROVAL_LEGAL_NOTICE}
        />
      )}
      <Card cardType="secondary" padding={{ default: 'sm', lg: 'lg' }}>
        <WizardContent>
          <ReasonsStep
            id={Steps.ReasonsStep}
            label={Steps.ReasonsStep}
            selectedDeclineReasons={selectedDeclineReasons}
            setSelectedDeclineReasons={setSelectedDeclineReasons}
            allDeclineReasons={allDeclineReasons}
          />
          <PreviewStep
            id={Steps.PreviewStep}
            label={Steps.PreviewStep}
            selectedDeclineReasons={selectedDeclineReasons}
            isBasedOnScreening={isBasedOnScreening}
            allDeclineReasons={allDeclineReasons}
          />
          <ConfirmationStep
            id={Steps.ConfirmationStep}
            label={Steps.ConfirmationStep}
            isBasedOnScreening={isBasedOnScreening}
          />
          <WithdrawnErrorStep id={Steps.WithdrawnErrorStep} label={Steps.WithdrawnErrorStep} />
          <StepNavigation />
        </WizardContent>
      </Card>
    </WizardProvider>
  );
};

export default DeclineApplicationPage;
